import { DataLoadingComponent } from '@/components/data-loading/data-loading.component';
import { TimelineEvent } from '@/components/timeline/timeline.component';
import { ObjectUpdatedType } from '@/enums/ObjectUpdatedType.enum';
import { TrackingHelper } from '@/helpers/tracking.helper';
import { UserHelper } from '@/helpers/user.helper';
import { AlertService } from '@/services/alert.service';
import { AppConfigService } from '@/services/app-config.service';
import { NgFor, NgIf } from '@angular/common';
import { Component, Inject, Input, LOCALE_ID, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { PropertyEvent, PropertyEventType, RealProperty, TrackingService } from '@eemo/api-client';
import { Subscription } from 'rxjs';
import { ConfirmDialogComponent } from '../../../components/confirm-dialog/confirm-dialog.component';
import { TimelineEventDialogComponent } from '../../../components/timeline-event-dialog/timeline-event-dialog.component';
import { TimelineComponent } from '../../../components/timeline/timeline.component';

@Component({
  selector: 'app-tracking-timeline',
  templateUrl: './tracking-timeline.component.html',
  styleUrls: ['./tracking-timeline.component.scss'],
  standalone: true,
  imports: [
    NgIf,
    MatFormFieldModule,
    MatIconModule,
    MatSelectModule,
    NgFor,
    MatOptionModule,
    TimelineComponent,
    MatButtonModule,
    DataLoadingComponent
  ]
})
export class TrackingTimelineComponent implements OnInit, OnDestroy {
  @Input() prop: RealProperty;
  @ViewChild('filersSelect', { static: true }) filtersSelect: MatSelect;

  propEvents: { [key: string]: PropertyEvent } = {};
  events: TimelineEvent[];
  canUpdateProperty = false;

  loading = false;

  filters: string[] = [];

  sub = new Subscription();
  updateSub = new Subscription();

  constructor(
    private dialog: MatDialog,
    private trackingService: TrackingService,
    public trackingHelper: TrackingHelper,
    @Inject(LOCALE_ID) public locale: string,
    public userHelper: UserHelper,
    private alertService: AlertService,
    private appConfigService: AppConfigService
  ) {}

  ngOnInit() {
    this.canUpdateProperty = this.userHelper.canUpdateProperty(this.prop);
    this.loadData();
    this.updateSub = this.appConfigService.objectUpdated.subscribe((type) => {
      if (
        [
          ObjectUpdatedType.EVENT,
          ObjectUpdatedType.WORK,
          ObjectUpdatedType.MAINTENANCE,
          ObjectUpdatedType.EQUIPMENT
        ].includes(type)
      ) {
        this.loadData();
      }
    });
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  loadData() {
    this.loading = true;
    let realFilters: PropertyEventType[] = [
      'WORK',
      'MAINTENANCE',
      'EQUIPMENT',
      'DISASTER',
      'ENVIRONMENT',
      'BUILDING',
      'PURCHASE'
    ];
    if (this.filters.length > 0) {
      realFilters = [];
      this.filters.forEach((eventType) => {
        switch (eventType) {
          case 'EEMO':
            realFilters.push('CREATION', 'CERTIFICATION', 'CERTIFICATION_RENEW', 'OWNERSHIP_TRANSFER');
            break;
          case 'BUILDING':
            realFilters.push('BUILDING');
            break;
          case 'DISASTER':
            realFilters.push('DISASTER');
            break;
          case 'ENVIRONMENT':
            realFilters.push('ENVIRONMENT');
            break;
          case 'WORK':
            realFilters.push('WORK');
            break;
          case 'MAINTENANCE':
            realFilters.push('MAINTENANCE');
            break;
          case 'EQUIPMENT':
            realFilters.push('EQUIPMENT');
            break;
          case 'PURCHASE':
            realFilters.push('PURCHASE');
            break;
        }
      });
    }
    this.events = [];
    this.sub = this.trackingService.getPropertyEvents(this.prop.uid, realFilters).subscribe({
      next: (data) => {
        data.forEach((e) => {
          const isEemo = this.trackingHelper.isEEMOEventType(e.type);
          const tIcons = [];
          if (e.auto) {
            tIcons.push({ matIcon: 'check_circle', tooltip: 'Événement ajouté automatiquement' });
          }
          if (e.energyImpact) {
            tIcons.push({ matIcon: 'battery_charging_full', tooltip: "A un impact sur la consommation d'énergie" });
          }

          const tEvent: TimelineEvent = {
            eventId: e.uid,
            date: e.date,
            title: isEemo ? '' : this.trackingHelper.getPropertyEventTypeName(e.type),
            details: isEemo ? this.trackingHelper.getPropertyEventTypeName(e.type) : e.details,
            svgIcon: this.getEventIcon(e.type),
            leftIcons: tIcons,
            canUpdate: this.canUpdateProperty && !e.auto,
            canDelete: this.canUpdateProperty && !e.auto
          };

          this.events.push(tEvent);
          this.propEvents[e.uid] = e;
        });
        this.loading = false;
      },
      error: () => (this.loading = false)
    });
  }

  getEventIcon(type: PropertyEventType): string {
    let icon;
    switch (type) {
      case 'BUILDING':
        icon = 'building.svg';
        break;
      case 'PURCHASE':
        icon = 'purchase.svg';
        break;
      case 'EQUIPMENT':
        icon = 'equipment.svg';
        break;
      case 'DISASTER':
        icon = 'disaster.svg';
        break;
      case 'ENVIRONMENT':
        icon = 'environment.svg';
        break;
      case 'WORK':
        icon = 'work.svg';
        break;
      case 'MAINTENANCE':
        icon = 'maintenance.svg';
        break;
      case 'CREATION':
        icon = 'creation.svg';
        break;
      case 'CERTIFICATION':
        icon = 'certification.svg';
        break;
      case 'CERTIFICATION_RENEW':
        icon = 'certification.svg';
        break;
      case 'OWNERSHIP_TRANSFER':
        icon = 'ownership_transfert.svg';
        break;
      default:
        icon = 'unknown.svg';
    }
    return 'assets/images/timeline/' + icon;
  }

  filterChange(event) {
    this.loadData();
  }

  addTimelineEvent() {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(TimelineEventDialogComponent, {
        // width: '20rem'
        data: { prop: this.prop }
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.loadData();
        }
      });
    }
  }

  edit(eventId: string) {
    const event = this.propEvents[eventId];
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(TimelineEventDialogComponent, {
        // width: '20rem'
        data: { event, edition: true }
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.loadData();
        }
      });
    }
  }

  confirmDeleteEvent(eventId: string) {
    const event = this.propEvents[eventId];
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        // width: '25rem',
        data: {
          message: "Confirmez-vous la suppression de l'événement ?"
        }
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.doDeleteEvent(event);
        }
      });
    }
  }

  doDeleteEvent(event: PropertyEvent) {
    this.trackingService.deletePropertyEvent(event.uid).subscribe((result) => {
      this.alertService.success('Suppression effectuée avec succès');
      this.loadData();
    });
  }
}
